import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';
const PROMO_DEADLINE = new Date('2026-09-01T23:59:59-03:00').getTime();
function getRemaining() {
  const diff = Math.max(0, PROMO_DEADLINE - Date.now());
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor(diff / (1000 * 60 * 60) % 24);
  const minutes = Math.floor(diff / (1000 * 60) % 60);
  const seconds = Math.floor(diff / 1000 % 60);
  return {
    days,
    hours,
    minutes,
    seconds
  };
}
function CountdownUnit({
  value,
  label
}: {
  value: number;
  label: string;
}) {
  return <div className="flex flex-col items-center" data-id="element-396" data-mp="eqymhpag8apg">
      <div className="w-full min-w-[64px] md:min-w-[76px] rounded-xl bg-noronha-volcanic/60 border border-noronha-petroleum/40 py-3 md:py-4" data-id="element-397" data-mp="isa2r47fnjkz">
        <span className="font-heading text-2xl md:text-3xl font-bold text-noronha-text-heading tabular-nums tracking-tight" data-id="element-398" data-mp="nsejmvc96gbq">
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <span className="mt-2 font-mono text-[10px] md:text-xs tracking-[0.18em] uppercase text-noronha-text-body/70" data-id="element-399" data-mp="he5rbl2pzhve">
        {label}
      </span>
    </div>;
}
function PromoCountdown() {
  const [time, setTime] = useState(getRemaining);
  useEffect(() => {
    const id = setInterval(() => setTime(getRemaining()), 1000);
    return () => clearInterval(id);
  }, []);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.6,
    ease: 'easeOut'
  }} className="rounded-3xl bg-noronha-graphite/60 border border-noronha-emerald/20 p-7 md:p-9 text-center mb-12 md:mb-14" data-id="element-400" data-mp="1020xais3brdy">
      <span className="font-mono text-xs md:text-sm tracking-[0.22em] uppercase text-noronha-emerald block mb-3" data-id="element-401" data-mp="1jsmgrwla7k36">
        Tarifa anticipada disponible
      </span>
      <p className="text-noronha-text-body text-sm md:text-base leading-relaxed max-w-md mx-auto mb-7" data-id="element-402" data-mp="1xw3di2xohtrc">
        Reservá antes del 1 de septiembre y accedé a los valores especiales de
        lanzamiento.
      </p>
      <div className="flex items-start justify-center gap-3 md:gap-4 mb-7" data-id="element-403" data-mp="1p38fnuajj0bl">
        <CountdownUnit value={time.days} label="Días" data-id="element-404" data-mp="1t5d5ehxvbcsy" />
        <CountdownUnit value={time.hours} label="Horas" data-id="element-405" data-mp="1t41zabgy9syo" />
        <CountdownUnit value={time.minutes} label="Minutos" data-id="element-406" data-mp="1f5u7sj2onjuw" />
        <CountdownUnit value={time.seconds} label="Segundos" data-id="element-407" data-mp="1k56w7897cyzl" />
      </div>
      <p className="text-noronha-text-body/60 text-xs md:text-sm leading-relaxed max-w-md mx-auto" data-id="element-408" data-mp="1k6i1jciaqest">
        La tarifa anticipada finaliza el 1 de septiembre. Luego se aplicarán los
        valores regulares del viaje.
      </p>
    </motion.div>;
}
type PackId = 'buceador' | 'explorador';
type Pack = {
  id: PackId;
  name: string;
  subtitle: string;
  price: string;
  regularPrice: string;
  savings: string;
  includes: string[];
  cta: string;
  checkoutUrl?: string;
};
const packs: Record<PackId, Pack> = {
  buceador: {
    id: 'buceador',
    name: 'Pack Buceador',
    subtitle: 'Experiencia completa de buceo',
    price: '2150',
    regularPrice: '2290',
    savings: '140',
    includes: ['7 noches de alojamiento', 'Desayunos', 'Almuerzos los días de buceo', '10 inmersiones embarcadas', 'Ilha Tour', 'Paseo exclusivo en barco', 'Traslados aeropuerto', 'Staff acompañando todo el viaje'],
    cta: 'Reservar Pack Buceador',
    checkoutUrl: 'https://checkout.librofy.online/cart/53372407873899:1'
  },
  explorador: {
    id: 'explorador',
    name: 'Pack Explorador',
    subtitle: 'Experiencia completa en la isla',
    price: '1290',
    regularPrice: '1490',
    savings: '200',
    includes: ['7 noches de alojamiento', 'Desayunos', 'Almuerzos incluidos', 'Ilha Tour', 'Paseo exclusivo en barco', 'Traslados aeropuerto', 'Actividades grupales', 'Staff acompañando todo el viaje'],
    cta: 'Reservar Pack Explorador',
    checkoutUrl: 'https://checkout.librofy.online/cart/53372410167659:1'
  }
};
function PackOption({
  pack,
  selected,
  onSelect
}: {
  pack: Pack;
  selected: boolean;
  onSelect: () => void;
}) {
  return <button type="button" onClick={onSelect} aria-pressed={selected} className={`w-full flex items-center gap-4 text-left rounded-2xl border p-5 md:p-6 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-noronha-emerald/60 ${selected ? 'border-noronha-emerald bg-noronha-emerald/5' : 'border-noronha-petroleum/40 bg-noronha-graphite hover:border-noronha-emerald/50'}`} data-id="element-409" data-mp="2o7k2z6icf8f">
      <span className={`shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors duration-300 ${selected ? 'border-noronha-emerald' : 'border-noronha-text-body/40'}`} aria-hidden="true" data-id="element-410" data-mp="3fvsiz19ucq4j">
        {selected && <span className="w-3 h-3 rounded-full bg-noronha-emerald" data-id="element-411" data-mp="2funsnru04iga" />}
      </span>
      <span className="flex flex-col" data-id="element-412" data-mp="2auronslb0r0q">
        <span className="font-heading text-lg md:text-xl text-noronha-text-heading tracking-tight" data-id="element-413" data-mp="2avubnpfia9bd">
          {pack.name}
        </span>
        <span className="text-noronha-text-body text-sm md:text-base" data-id="element-414" data-mp="20x53iodew38b">
          {pack.subtitle}
        </span>
      </span>
    </button>;
}
function PriceCard({
  pack
}: {
  pack: Pack;
}) {
  return <motion.div key={pack.id} initial={{
    opacity: 0,
    y: 16
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -12
  }} transition={{
    duration: 0.35,
    ease: 'easeOut'
  }} className="bg-white rounded-3xl shadow-[0_24px_60px_rgba(2,6,23,0.45)] p-8 md:p-10" data-id="element-415" data-mp="1s4tg7bdlgvwk">
      <span className="font-mono text-xs md:text-sm tracking-[0.22em] uppercase text-noronha-emerald block mb-5" data-id="element-416" data-mp="1d9djszpjknx0">
        {pack.name}
      </span>

      <span className="inline-flex items-center gap-1.5 rounded-full bg-noronha-emerald/10 text-noronha-emerald font-mono text-[10px] md:text-xs tracking-[0.14em] uppercase px-3 py-1.5 mb-4" data-id="element-417" data-mp="yjsiuzk8h3xf">
        Tarifa anticipada hasta el 01/09
      </span>

      <div className="flex items-baseline gap-2 mb-1" data-id="element-418" data-mp="pmiapn2ppuwq">
        <span className="text-sm md:text-base font-medium text-slate-400 line-through" data-id="element-419" data-mp="uk1lue445rkg">
          USD {pack.regularPrice}
        </span>
      </div>
      <div className="flex items-baseline gap-2 mb-2" data-id="element-420" data-mp="1maesuwdssya2">
        <span className="text-xl md:text-2xl font-medium text-slate-400" data-id="element-421" data-mp="1m94ue6319o2w">
          Total USD
        </span>
        <span className="font-heading text-5xl md:text-6xl font-bold text-slate-900 tracking-tight" data-id="element-422" data-mp="18ii1iujyeseq">
          {pack.price}
        </span>
      </div>
      <p className="text-noronha-emerald text-sm md:text-base font-medium mb-7" data-id="element-423" data-mp="217yq1h4gdp5o">
        Ahorrás USD {pack.savings} reservando anticipadamente.
      </p>

      <ul className="flex flex-col gap-3 mb-8" data-id="element-424" data-mp="25zqbxecnz6ph">
        {pack.includes.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-425" data-mp="2fbnxp9tewu2e" data-mpi={i}>
            <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-noronha-emerald/15 flex items-center justify-center" data-id="element-426" data-mp="2bc5dyhdbup15">
              <Check className="w-3 h-3 text-noronha-emerald" strokeWidth={3} data-id="element-427" data-mp="2p1pf5ir9fe8c" />
            </span>
            <span className="text-slate-600 text-sm md:text-base leading-relaxed" data-id="element-428" data-mp="2k58qmng5exuq">
              {item}
            </span>
          </li>)}
      </ul>

      <button onClick={() => {
      if (pack.checkoutUrl) {
        window.open(pack.checkoutUrl, '_blank', 'noopener');
      }
    }} className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-noronha-emerald text-noronha-volcanic font-heading font-bold text-sm md:text-base tracking-wide uppercase hover:bg-noronha-emerald/90 hover:shadow-[0_0_20px_rgba(31,163,122,0.35)] transition-all duration-300" data-id="element-429" data-mp="1sd18tjowpfer">
        {pack.cta}
      </button>
    </motion.div>;
}
export function Reservas() {
  const [selected, setSelected] = useState<PackId>('buceador');
  return <section id="reservar" className="relative bg-noronha-volcanic py-24 md:py-32" data-id="element-430" data-mp="1f62dchzri6pq">
      <div className="container mx-auto px-6" data-id="element-431" data-mp="1k3c01pvro49v">
        {/* Main reservation card */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} className="max-w-xl mx-auto bg-white rounded-3xl shadow-[0_24px_60px_rgba(2,6,23,0.45)] p-8 md:p-12 text-center" data-id="element-432" data-mp="1ov3r01iu4c0j">
          <span className="font-mono text-xs md:text-sm tracking-[0.22em] uppercase text-noronha-emerald block mb-6" data-id="element-433" data-mp="8hdnavvmnprf">
            Reservá tu lugar
          </span>
          <div className="flex items-baseline justify-center gap-2 mb-8" data-id="element-434" data-mp="dhi4ko4d0oip">
            <span className="text-2xl md:text-3xl font-medium text-slate-400" data-id="element-435" data-mp="5xgzlk29as3d">
              Seña USD
            </span>
            <span className="font-heading text-6xl md:text-7xl font-bold text-slate-900 tracking-tight" data-id="element-436" data-mp="13mj0phy6t8i">
              100
            </span>
          </div>

          <p className="text-slate-500 text-sm md:text-base leading-relaxed mb-8" data-id="element-437" data-mp="3ndyu4tlze3xb">
            Una vez realizada la reserva nos pondremos en contacto para
            coordinar los siguientes pagos y responder cualquier consulta sobre
            el viaje.
          </p>

          <p className="text-slate-900 font-heading font-semibold text-base md:text-lg leading-relaxed mb-6" data-id="element-438" data-mp="ewb21r29qcou">
            Reservá tu lugar con solo USD 100 y asegurá tu participación en uno
            de los destinos más exclusivos del planeta.
          </p>

          <div className="rounded-2xl bg-noronha-emerald/5 border border-noronha-emerald/20 p-5 mb-8 text-left" data-id="element-439" data-mp="2zkytr18olljg">
            <p className="text-slate-900 text-sm md:text-base font-medium leading-relaxed flex items-start gap-2" data-id="element-440" data-mp="1xowim2js97h5">
              <span className="text-lg leading-none shrink-0" data-id="element-441" data-mp="1tnkxoabutafa">🔥</span>
              <span data-id="element-442" data-mp="1tli0n6caah12">
                Reservá antes del 1 de septiembre y accedé a la tarifa anticipada
                exclusiva.
              </span>
            </p>
            <p className="text-slate-500 text-xs md:text-sm leading-relaxed mt-2" data-id="element-443" data-mp="22n7usjxjvu2j">
              Los valores promocionales son limitados y están sujetos a
              disponibilidad.
            </p>
          </div>

          <button onClick={() => document.getElementById('elegir-pack')?.scrollIntoView({
          behavior: 'smooth'
        })} className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-noronha-emerald text-noronha-volcanic font-heading font-bold text-base tracking-wide hover:bg-noronha-emerald/90 hover:shadow-[0_0_20px_rgba(31,163,122,0.35)] transition-all duration-300" data-id="element-444" data-mp="2cdjaf3qljrp9">
            <span className="text-lg leading-none" data-id="element-445" data-mp="32ocyqw6bc099">🤿</span>
            Quiero sumarme al grupo
          </button>
        </motion.div>

        {/* Selection section */}
        <div id="elegir-pack" className="max-w-xl mx-auto mt-20 md:mt-28 scroll-mt-24" data-id="element-446" data-mp="2twb093ft0u1m">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} className="text-center mb-10 md:mb-12" data-id="element-447" data-mp="2xvs5pwv8dr6s">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-noronha-text-heading tracking-tight text-balance mb-5" data-id="element-448" data-mp="95ag2m60bsfp">
              Elegí cómo querés vivir Fernando de Noronha
            </h2>
            <p className="text-noronha-text-body text-lg text-balance" data-id="element-449" data-mp="1m3kehjzf451f">
              Seleccioná la experiencia que mejor se adapte a vos para continuar
              con la reserva.
            </p>
          </motion.div>

          <PromoCountdown data-id="element-450" data-mp="1d45soxu74in9" />

          <div className="flex flex-col gap-4 mb-10" data-id="element-451" data-mp="1d3e8ej61u7ca">
            <PackOption pack={packs.buceador} selected={selected === 'buceador'} onSelect={() => setSelected('buceador')} data-id="element-452" data-mp="184kvosyl6kd6" />
            <PackOption pack={packs.explorador} selected={selected === 'explorador'} onSelect={() => setSelected('explorador')} data-id="element-453" data-mp="20soy95lv2hbn" />
          </div>

          <AnimatePresence mode="wait" data-id="element-454" data-mp="1r2x60i18i0ac">
            <PriceCard pack={packs[selected]} data-id="element-455" data-mp="1r244pwl1o2zl" />
          </AnimatePresence>
        </div>
      </div>
    </section>;
}